import { useCallback, useEffect, useState } from 'react';

import { mockNextPayment } from '../mock-data';
import type { NextPaymentDate } from '../types';

const SIMULATED_DELAY_MS = 400;

/** Set to true (e.g. via ?simulateError=1) to test error state. */
const getSimulateError = (): boolean =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('simulateError') === '1';

export type UseNextPaymentDateResult = {
  data: NextPaymentDate | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useNextPaymentDate(): UseNextPaymentDateResult {
  const [data, setData] = useState<NextPaymentDate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchNextPayment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await new Promise(resolve => setTimeout(resolve, SIMULATED_DELAY_MS));
      if (getSimulateError()) {
        throw new Error('Simulated error');
      }
      setData(mockNextPayment);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNextPayment();
  }, [fetchNextPayment]);

  return { data, isLoading, error, refetch: fetchNextPayment };
}
